import { useEffect, useRef, useState } from 'react'

import {
  Drawer,
  Header,
  type InitialUploadFile,
  PrimaryButton,
  SingleFileViewerUpload,
  Space,
  TertiaryButton,
  type UploadFile,
} from '@procurify/ui'

import { OTHER_PAYMENT_OPTION_ID } from '@webapp/components/RequestableCreditCardSelect'
import { type Currency } from '@webapp/core/entities/external/Currency'
import { useFormatMedia } from '@webapp/core/hooks/useFormatMedia'
import { useProcurifyIntl } from '@webapp/hooks'
import { computeExpenseLineItemTotalsUseCase } from '@webapp/modules/expense/business-logic/use-cases/ComputeExpenseLineItemTotals'
import { isCustomTax } from '@webapp/modules/expense/components/ExpenseTaxSelect'
import { type ExpenseFormReimbursementCustomization } from '@webapp/modules/expense/entities/enums'
import {
  ExpensePaymentTypes,
  type ExpensePaymentTypeEnum,
} from '@webapp/modules/expense/entities/enums/ExpensePaymentTypeEnum'
import { ExpenseTypes } from '@webapp/modules/expense/entities/enums/ExpenseTypeEnum'
import { type ExpenseItem } from '@webapp/modules/expense/entities/ExpenseItem'
import { type RequestMileageConfiguration } from '@webapp/modules/expense/entities/RequestMileageConfiguration'
import messages from './messages'
import { ExpenseLineItemDrawerContentStyled } from './styles/ExpenseLineItemDrawerContentStyled'
import { ExpenseLineItemDrawerDocumentStyled } from './styles/ExpenseLineItemDrawerDocumentStyled'
import { ExpenseLineItemDrawerFooterStyled } from './styles/ExpenseLineItemDrawerFooterStyled'
import { type ExpenseItemFormSchema } from './types'
import {
  ExpenseLineItemForm,
  type ExpenseLineItemFormHandle,
  type ExpenseLineItemFormSchema,
} from '../ExpenseLineItemForm'

export interface IExpenseLineItemDrawerProps {
  drawerOpen: boolean
  onClose: () => void
  onSubmit: (values: ExpenseItemFormSchema) => void
  item?: ExpenseItem
  reimbursementSetting: ExpenseFormReimbursementCustomization
  requesterId: number
  secondaryCategoryId?: number
  baseCurrency?: Nullable<Currency>
  mileageConfiguration?: RequestMileageConfiguration | null
}

const buildExpenseTypeData = (
  values: ExpenseLineItemFormSchema
): ExpenseItemFormSchema['expenseTypeData'] => {
  switch (values.expenseType) {
    case ExpenseTypes.MEALS:
      return { type: 'MEALS', data: { tips: values.tips } }
    case ExpenseTypes.MILEAGE:
      return {
        type: 'MILEAGE',
        data: {
          mileageRate: values.mileageRate,
          distance: values.distance,
          unitOfDistance: values.unitOfDistanceStr.value as 'km' | 'mi',
        },
      }
    case ExpenseTypes.LABOUR:
      return {
        type: 'LABOUR',
        data: {
          hourlyRate: values.hourlyRate,
          hoursOfWork: values.hoursOfWork,
        },
      }
    case ExpenseTypes.AIRFARE:
      return {
        type: 'AIRFARE',
        data: {
          airline: values.airline,
          ticketNumber: values.ticketNumber,
          departureDate: values.departureDate
            ? values.departureDate.format('YYYY-MM-DD')
            : null,
          arrivalDate: values.arrivalDate
            ? values.arrivalDate.format('YYYY-MM-DD')
            : null,
        },
      }
    case ExpenseTypes.LODGING:
      return { type: 'LODGING', data: null }
    default:
      return { type: 'OTHER', data: null }
  }
}

const mapFormSchemaToExpenseItemFormSchema = (
  values: ExpenseLineItemFormSchema,
  item: ExpenseItem | undefined
): ExpenseItemFormSchema => {
  if (values.currency == null) {
    // Yup validation requires currency before submit, so this path only
    // fires when validation has been bypassed.
    throw new Error('ExpenseLineItemForm submitted without a currency')
  }

  const taxTypeValue = values.taxType
  const isCustom = isCustomTax(taxTypeValue)

  const { computedTotal } = computeExpenseLineItemTotalsUseCase.execute({
    expenseType: values.expenseType,
    subtotal: values.subtotal,
    tax: isCustom
      ? { mode: 'custom', percentage: taxTypeValue.percentage }
      : {
          mode: taxTypeValue.id === '$' ? 'dollar' : 'percent',
          amount: values.taxAmount,
        },
    tips: values.tips,
    mileageRate: values.mileageRate,
    distance: values.distance,
    hourlyRate: values.hourlyRate,
    hoursOfWork: values.hoursOfWork,
  })

  const taxMode: ExpenseItemFormSchema['taxMode'] = isCustom
    ? 'saved'
    : taxTypeValue.id === '%'
      ? 'percent'
      : 'dollar'

  const taxName = isCustom ? taxTypeValue.name : taxTypeValue.id
  const taxPercentage = isCustom
    ? taxTypeValue.percentage
    : taxTypeValue.id === '%'
      ? String(values.taxAmount / 100)
      : '0'
  const taxId = isCustom ? taxTypeValue.id : null

  // Reimbursable items do not collect a payment method from the UI, so any
  // stale selection is cleared and the original paymentType is preserved on
  // edit (OTHER for new items). For non-reimbursable items with no selection
  // (e.g. CASH/CHEQUE on edit, which have no matching Select option), preserve
  // the item's original paymentType rather than silently coercing to OTHER.
  const resolvedPaymentMethod = values.reimbursable
    ? null
    : (values.paymentMethod ?? null)

  const resolvedPaymentType: ExpensePaymentTypeEnum = (() => {
    if (resolvedPaymentMethod == null) {
      return item?.paymentType ?? ExpensePaymentTypes.OTHER
    }
    if (resolvedPaymentMethod.id === OTHER_PAYMENT_OPTION_ID) {
      return ExpensePaymentTypes.OTHER
    }
    return ExpensePaymentTypes.CREDIT_CARD_ASSIGNED
  })()

  return {
    id: item?.id ?? null,
    merchant: values.merchant,
    account: values.account ?? null,
    currency: values.currency,
    reimbursable: values.reimbursable,
    expenseType: values.expenseType,
    paymentType: resolvedPaymentType,
    paymentMethod: resolvedPaymentMethod,
    amount: String(computedTotal),
    taxMode,
    taxValue: String(values.taxAmount),
    taxId,
    taxName,
    taxPercentage,
    transactionDate: values.transDate
      ? values.transDate.format('YYYY-MM-DD')
      : '',
    attachment: values.attachment,
    notes: values.notes,
    expenseTypeData: buildExpenseTypeData(values),
  }
}

export const ExpenseLineItemDrawer = ({
  drawerOpen,
  onClose,
  onSubmit,
  item,
  reimbursementSetting,
  requesterId,
  secondaryCategoryId,
  baseCurrency,
  mileageConfiguration = null,
}: IExpenseLineItemDrawerProps) => {
  const intl = useProcurifyIntl()
  const formatMedia = useFormatMedia()
  const formRef = useRef<ExpenseLineItemFormHandle | null>(null)

  const initialAttachment = item?.attachment ?? ''
  const [currentAttachment, setCurrentAttachment] =
    useState<string>(initialAttachment)

  useEffect(() => {
    setCurrentAttachment(item?.attachment ?? '')
  }, [item])

  const handleUploadComplete = (file: UploadFile) => {
    // Use the server-returned file path ("attachments/<uuid>") so Django
    // FieldFile.url can construct a valid S3 URL on server-side PDF/export
    // paths. Persisting a bare UUID reads back fine in-app (via the
    // /attachments/{uuid}/ resolver) but breaks PO / Remittance rendering.
    // Fail visibly when the response omits .file rather than falling back to
    // the UUID. See PLAT-4061 / PR #9895.
    const filePath = file.rawResponse?.data?.file
    setCurrentAttachment(filePath != null ? String(filePath) : '')
  }

  const handleDeleteFile = () => {
    setCurrentAttachment('')
  }

  const handleFormSubmit = (values: ExpenseLineItemFormSchema) => {
    const mapped = mapFormSchemaToExpenseItemFormSchema(values, item)
    onSubmit(mapped)
  }

  const initialFile: InitialUploadFile | undefined = currentAttachment
    ? {
        id: currentAttachment,
        file: formatMedia(currentAttachment),
      }
    : undefined

  const drawerTitle =
    item != null
      ? intl.formatMessage(messages.editDrawerTitle)
      : intl.formatMessage(messages.addDrawerTitle)

  const renderDocument = () => (
    <ExpenseLineItemDrawerDocumentStyled>
      <SingleFileViewerUpload
        initialFile={initialFile}
        onUploadComplete={handleUploadComplete}
        onDelete={handleDeleteFile}
        disabled={false}
      />
    </ExpenseLineItemDrawerDocumentStyled>
  )

  return (
    <Drawer
      drawerOpen={drawerOpen}
      setDrawerOpen={onClose}
      minWidth='530px'
      documentOpen={true}
      DocumentProps={{
        renderDocument,
        closeDrawerOnEsc: true,
      }}
      DrawerProps={{
        renderTitle: () => <Header as='h4'>{drawerTitle}</Header>,
        renderFooter: () => (
          <ExpenseLineItemDrawerFooterStyled>
            <Space direction='horizontal' justify='start' align='center'>
              <PrimaryButton
                type='button'
                onClick={() => formRef.current?.submitForm()}
              >
                {intl.formatMessage(messages.saveButton)}
              </PrimaryButton>
              <TertiaryButton type='button' onClick={onClose}>
                {intl.formatMessage(messages.cancelButton)}
              </TertiaryButton>
            </Space>
          </ExpenseLineItemDrawerFooterStyled>
        ),
      }}
    >
      {() => (
        <ExpenseLineItemDrawerContentStyled>
          <ExpenseLineItemForm
            key={item?.id ?? 'new'}
            item={item}
            reimbursementSetting={reimbursementSetting}
            secondaryCategoryId={secondaryCategoryId}
            requesterId={requesterId}
            attachment={currentAttachment}
            isSubmitting={false}
            onSubmit={handleFormSubmit}
            formRef={formRef}
            baseCurrency={baseCurrency}
            mileageConfiguration={mileageConfiguration}
          />
        </ExpenseLineItemDrawerContentStyled>
      )}
    </Drawer>
  )
}

ExpenseLineItemDrawer.displayName = 'ExpenseLineItemDrawer'
